import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

// Per-night prices from Exely's public availability calendar API (same feed as their
// own booking calendar). CORS is open (access-control-allow-origin: *). Cached per month.
const monthCache = new Map();

const monthKey = (year, month) => `${year}-${String(month + 1).padStart(2, "0")}`;

const fetchMonthPrices = async (hotelCode, year, month) => {
  const key = monthKey(year, month);
  if (monthCache.has(key)) return monthCache.get(key);
  const lastDay = new Date(year, month + 1, 0).getDate();
  const url =
    `https://id-ibe.hopenapi.com/ApiWebDistribution/AvailabilityCalendar/room_type_availability_2` +
    `?currency=IDR&hotel=${hotelCode}&start_date=${key}-01&end_date=${key}-${lastDay}&max_nights=21&audience=BookingForm`;
  const prices = {};
  try {
    const res = await fetch(url);
    const data = await res.json();
    (data.room_type_availability || []).forEach((rt) => {
      (rt.availability_date || []).forEach((d) => {
        const day = d.period?.start_date;
        const price = d.price?.price_after_tax;
        if (!day || !d.is_available || !price) return;
        if (!prices[day] || price < prices[day]) prices[day] = price;
      });
    });
  } catch (e) {
    /* calendar still works without prices */
  }
  monthCache.set(key, prices);
  return prices;
};

const formatCompact = (price, lang) => {
  if (!price) return "";
  if (price >= 1000000) {
    const m = price / 1000000;
    const rounded = Math.round(m * 10) / 10;
    return lang === "id" ? `${String(rounded).replace(".", ",")} jt` : `${rounded}m`;
  }
  const k = Math.round(price / 1000);
  return lang === "id" ? `${k} rb` : `${k}k`;
};

const toISO = (year, month, day) => `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;

export default function PriceCalendar({ hotelCode, value, onSelect, lang }) {
  const initial = value ? new Date(`${value}T00:00:00`) : new Date();
  const [view, setView] = useState({ year: initial.getFullYear(), month: initial.getMonth() });
  const [prices, setPrices] = useState({});
  const [loading, setLoading] = useState(false);

  const today = new Date();
  const todayISO = toISO(today.getFullYear(), today.getMonth(), today.getDate());

  useEffect(() => {
    if (!hotelCode) return undefined;
    let cancelled = false;
    setLoading(true);
    // Load the viewed month and the next one so prices are ready when paging
    const next = view.month === 11 ? { year: view.year + 1, month: 0 } : { year: view.year, month: view.month + 1 };
    Promise.all([fetchMonthPrices(hotelCode, view.year, view.month), fetchMonthPrices(hotelCode, next.year, next.month)]).then(
      ([current, upcoming]) => {
        if (cancelled) return;
        setPrices((prev) => ({ ...prev, ...current, ...upcoming }));
        setLoading(false);
      }
    );
    return () => {
      cancelled = true;
    };
  }, [view, hotelCode]);

  const grid = useMemo(() => {
    const first = new Date(view.year, view.month, 1);
    const daysInMonth = new Date(view.year, view.month + 1, 0).getDate();
    const leadBlanks = (first.getDay() + 6) % 7; // Monday-first
    const cells = [];
    for (let i = 0; i < leadBlanks; i += 1) cells.push(null);
    for (let d = 1; d <= daysInMonth; d += 1) cells.push(d);
    return cells;
  }, [view]);

  const monthLabel = new Date(view.year, view.month, 1).toLocaleDateString(lang === "id" ? "id-ID" : "en-GB", {
    month: "long",
    year: "numeric",
  });
  const isCurrentMonth = view.year === today.getFullYear() && view.month === today.getMonth();
  const weekDays = lang === "id" ? ["Sen", "Sel", "Rab", "Kam", "Jum", "Sab", "Min"] : ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

  const shiftMonth = (delta) => {
    setView((v) => {
      const m = v.month + delta;
      if (m < 0) return { year: v.year - 1, month: 11 };
      if (m > 11) return { year: v.year + 1, month: 0 };
      return { year: v.year, month: m };
    });
  };

  return (
    <div data-testid="booking-calendar-panel" className="bg-[#FBF9F5] text-[#2C2825] rounded-xl shadow-[0_24px_70px_rgba(30,26,23,0.35)] border border-[#E5DED4] p-4 w-[300px]">
      <div className="flex items-center justify-between mb-3">
        <button
          type="button"
          data-testid="booking-calendar-prev"
          onClick={() => shiftMonth(-1)}
          disabled={isCurrentMonth}
          className="p-1.5 rounded-full hover:bg-[#F4EFEA] disabled:opacity-30 disabled:cursor-default transition-colors"
          aria-label="Previous month"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        <span data-testid="booking-calendar-month" className="font-serif text-base text-[#2C2825]">{monthLabel}</span>
        <button
          type="button"
          data-testid="booking-calendar-next"
          onClick={() => shiftMonth(1)}
          className="p-1.5 rounded-full hover:bg-[#F4EFEA] transition-colors"
          aria-label="Next month"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
      <div className="grid grid-cols-7 gap-0.5 mb-1">
        {weekDays.map((d) => (
          <span key={d} className="text-center text-[9px] uppercase tracking-wider text-[#A0988E] font-sans font-semibold py-1">
            {d}
          </span>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-0.5">
        {grid.map((day, i) => {
          if (day === null) return <span key={`blank-${i}`} />;
          const iso = toISO(view.year, view.month, day);
          const isPast = iso < todayISO;
          const price = prices[iso];
          const selected = iso === value;
          return (
            <button
              key={iso}
              type="button"
              data-testid={`booking-calendar-day-${iso}`}
              disabled={isPast}
              onClick={() => onSelect(iso)}
              className={`flex flex-col items-center justify-center rounded-md py-1.5 min-h-[44px] transition-colors ${
                selected
                  ? "bg-[#9A7B66] text-white"
                  : isPast
                    ? "text-[#C9C0B4] cursor-default"
                    : "hover:bg-[#F4EFEA] text-[#2C2825]"
              }`}
            >
              <span className="text-[13px] font-sans leading-none">{day}</span>
              {!isPast && (
                <span
                  data-testid={`booking-calendar-price-${iso}`}
                  className={`text-[8.5px] font-sans leading-tight mt-0.5 ${selected ? "text-white/85" : "text-[#9A7B66]"}`}
                >
                  {price ? formatCompact(price, lang) : loading ? "…" : ""}
                </span>
              )}
            </button>
          );
        })}
      </div>
      {hotelCode && (
        <p className="mt-2.5 text-center text-[9px] text-[#A0988E] font-sans">
          {lang === "id" ? "Harga per malam mulai dari, dalam IDR" : "From-prices per night, in IDR"}
        </p>
      )}
    </div>
  );
}
